/*
 * CAPTURE-OWNED MODULE
 * Guided setup v2, slice 1b — pure decision logic behind the classification
 * evidence panel on ReviewDetailPage ("turn one": the machine says what it
 * saw, and asks whether it was right).
 *
 * Lives here for the same reason typeCorrection.js does: capture-ui has no RTL,
 * so every branch a test must pin is a plain function with no I/O. The network
 * calls stay in services/api.js and the fetching stays on the page.
 *
 * THE GOVERNING RULE OF THIS FILE — a missing key means UNKNOWN, never zero and
 * never empty. `matchedByType` and `winnerPhraseCount` arrived with slice 1a
 * (documentTypeInfer.ts) and the evidence blobs already in
 * capture_documents.document_type_evidence do NOT carry them, cannot be
 * backfilled (the phrase set a historical row was scored against is gone), and
 * are the ones a reviewer is most likely to be looking at. So the summary
 * distinguishes three states the UI must render differently:
 *
 *   runnersUp === null   we cannot know what else was considered (legacy blob)
 *   runnersUp === []     we know, and nothing else matched
 *   nothingMatched       we can PROVE the classifier matched nothing at all
 *
 * Collapsing the first into either of the others would make the panel state a
 * fact it does not have. The UI must say less, not guess.
 */

// Below this, the classifier's answer is not worth asserting to a human without
// asking. Mirrors the sub-0.5 band documentTypeInfer's below-threshold branch
// stores (capped 0.49) and the `confidence < 0.5` phrase-observation gate.
const LOW_CONFIDENCE = 0.5;

// At most three alternatives. The panel is a question, not a report; a fourth
// runner-up has never changed anyone's answer and pushes the actions below the
// fold of the left panel header.
const MAX_RUNNERS_UP = 3;

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stringList(value) {
  return Array.isArray(value) ? value.filter((v) => typeof v === 'string' && v !== '') : [];
}

/**
 * Should the panel open on its own, without the reviewer asking?
 *
 * It opens when the machine is not confident AND nobody has already answered.
 * `source === 'user'` means a human already asserted the type — re-asking is
 * both noise and an invitation to undo their own correction. A pending
 * declaration means the question has been answered with "it's something new"
 * and is queued for setup; asking again would offer a duplicate the server
 * 409s.
 *
 * NOTE the deliberate asymmetry on confidence: a null/undefined confidence on a
 * machine source does NOT auto-open. Five of nineteen live rows carry a NULL
 * evidence column and rows predate the confidence column entirely — "we did not
 * record a number" is not evidence of a bad answer, and auto-opening on it
 * would interrupt every historical document in the queue.
 */
export function shouldAutoOpen({ documentType, confidence, source, hasPendingDeclaration } = {}) {
  if (source === 'user') return false;
  if (hasPendingDeclaration) return false;
  if (documentType == null || documentType === 'unknown') return true;
  return typeof confidence === 'number' && confidence < LOW_CONFIDENCE;
}

/**
 * Reduce a stored evidence blob to exactly what the panel can honestly show.
 *
 * @param {unknown} evidence   document_type_evidence, as served. Tolerates
 *                             null, a string, and any missing key.
 * @param {string|null} winnerType  the document's classified type.
 * @returns {{winnerPhrases: string[], runnersUp: null|Array<{type: string, phrases: string[], score: number}>, nothingMatched: boolean, divisor: number|null}}
 */
export function summarizeEvidence(evidence, winnerType) {
  const blob = isPlainObject(evidence) ? evidence : null;
  const matchedByType = blob && isPlainObject(blob.matchedByType) ? blob.matchedByType : null;
  const scoresByType = blob && isPlainObject(blob.scoresByType) ? blob.scoresByType : null;

  // matchedByType is authoritative WHEN PRESENT, including when it holds no
  // entry for the winner — that is a real answer ("the winner matched nothing
  // through signals", which is how a dictionary force/boost win looks). Only
  // its total absence sends us to the flat, un-attributed matchedKeywords.
  const winnerPhrases = matchedByType
    ? stringList(winnerType == null ? null : matchedByType[winnerType])
    : stringList(blob && blob.matchedKeywords);

  // NULL when matchedByType is absent: a legacy blob recorded no attribution,
  // so "what else was considered" is unknowable, not empty. Iterating
  // matchedByType (never scoresByType) is also what skips the dictionary
  // pseudo-entries — a force/boost adds a scoresByType key for a type that was
  // never phrase-scored, and a type with no phrases is not an alternative a
  // reviewer can evaluate.
  const runnersUp = matchedByType
    ? Object.keys(matchedByType)
        .filter((type) => type !== winnerType)
        .map((type) => ({
          type,
          phrases: stringList(matchedByType[type]),
          score: typeof (scoresByType && scoresByType[type]) === 'number' ? scoresByType[type] : 0,
        }))
        .filter((r) => r.phrases.length > 0)
        .sort((a, b) => (b.score - a.score) || a.type.localeCompare(b.type))
        .slice(0, MAX_RUNNERS_UP)
    : null;

  // Provable only. `scoresByType` present with at least one entry, every score
  // zero, and the winner matched nothing: that is the classifier having looked
  // and found nothing. An absent or empty scoresByType is silence, not a zero —
  // and `every()` over an empty list is vacuously true, which is exactly the
  // wrong answer here.
  const scoreValues = scoresByType ? Object.values(scoresByType) : [];
  const nothingMatched =
    winnerPhrases.length === 0 &&
    scoreValues.length > 0 &&
    scoreValues.every((s) => s === 0);

  const divisor = blob && typeof blob.winnerPhraseCount === 'number' ? blob.winnerPhraseCount : null;

  return { winnerPhrases, runnersUp, nothingMatched, divisor };
}

/**
 * Confidence band for display. Same thresholds as the (still unused)
 * confidenceClass helper in ReviewDetailPage.jsx, deliberately including its
 * comparison shape: a null/undefined/NaN confidence falls through to 'low'
 * rather than throwing. Callers that must distinguish "no number recorded"
 * from "a low number" check the value itself — the panel does, and shows no
 * percent at all in that case.
 */
export function confidenceTier(conf) {
  if (conf >= 0.8) return 'high';
  if (conf >= 0.5) return 'medium';
  return 'low';
}
